// src/affinity_ops_write_api.js
// ─────────────────────────────────────────────────────────────────────────────
// WRITE LAYER — TIME, TASKS AND PROCEDURES
//
// Wrappers for db/059_write_time_tasks_procedures.sql: the things staff do
// daily rather than the entity registers covered by affinity_entity_write_api.
//
// Same contract as the entity writes — { ok, data, error, live } — so callers
// handle both the same way. Database validation messages are written to be
// read by the person who triggered them and are passed through unchanged.
// ─────────────────────────────────────────────────────────────────────────────
import { supabase, isConfigured } from "./affinity_accounting_supabase";

async function call(fn, args) {
  if (!isConfigured) {
    return { ok: false, live: false, data: null,
             error: "Not signed in — changes cannot be saved yet." };
  }
  try {
    const { data, error } = await supabase.rpc(fn, args || {});
    if (error) return { ok: false, live: true, data: null, error: clean(error.message) };
    return { ok: true, live: true, data, error: null };
  } catch (e) {
    return { ok: false, live: false, data: null, error: String((e && e.message) || e) };
  }
}
const clean = (m) => !m ? "That could not be saved."
  : String(m).replace(/^ERROR:\s*/i, "").replace(/\s*CONTEXT:[\s\S]*$/i, "").trim();

// ── Timesheets ──────────────────────────────────────────────────────────────
export const timeAdd = (staffId, e) => call("ts_entry_add", {
  p_staff_id: staffId, p_entry_date: e.date, p_entity_label: e.entity,
  p_matter: e.matter || null, p_entry_type: e.workType || null,
  p_hours: e.hours, p_billable: e.billable !== false,
  p_rate: e.rate ?? null, p_narrative: e.narrative || null,
});
export const timeUpdate = (id, e) => call("ts_entry_update", {
  p_id: id, p_hours: e.hours ?? null, p_matter: e.matter || null,
  p_narrative: e.narrative || null, p_billable: e.billable ?? null,
});
export const timeSubmit = (staffId, from, to) =>
  call("ts_entry_submit", { p_staff_id: staffId, p_from: from, p_to: to });
export const timeApprove = (ids, approve, reason) =>
  call("ts_entry_approve", { p_ids: ids, p_approve: !!approve, p_reason: reason || null });

// Converts a running timer into a recorded entry. Seconds become hours to two
// decimals, which is how the timesheet stores them.
export const timerToEntry = (staffId, e, seconds) =>
  timeAdd(staffId, { ...e, hours: Math.max(0.01, Math.round((seconds / 3600) * 100) / 100) });

// ── Tasks ───────────────────────────────────────────────────────────────────
export const taskAdd = (t) => call("task_add", {
  p_title: t.title, p_category: t.category || null, p_entity_label: t.entity || null,
  p_entity_id: t.entityId ?? null, p_assignee: t.assignee || null,
  p_due_date: t.dueDate || null, p_priority: t.priority || "Normal", p_notes: t.notes || null,
});
export const taskSetStatus = (id, status, notes) =>
  call("task_set_status", { p_id: id, p_status: status, p_notes: notes || null });
export const taskReassign = (id, assignee) =>
  call("task_reassign", { p_id: id, p_assignee: assignee });
export const taskList = (assignee, status) =>
  call("task_list", { p_assignee: assignee || null, p_status: status || null });

// Raising a task from an Activity item. The note records where it came from,
// which is the point of the button — otherwise the context is lost.
export const taskFromActivity = (item, assignee) => taskAdd({
  title: item.title,
  category: item.category || item.mod || "Follow-up",
  entity: item.entity || null,
  assignee: assignee || null,
  notes: "Raised from " + (item.mod || "Activity")
       + (item.who ? " · " + item.who : "")
       + (item.t ? " · " + item.t : ""),
});

// ── Notifications ───────────────────────────────────────────────────────────
export const notificationAdd = (n) => call("notification_add", {
  p_ntype: n.type || "info", p_title: n.title, p_body: n.body || null,
  p_who: n.who || null, p_mod: n.mod || null,
});

// ── Procedure runs ──────────────────────────────────────────────────────────
export const procStart = (p) => call("proc_run_start", {
  p_proc: p.proc, p_title: p.title || null, p_entity_label: p.entity || null,
  p_total: p.totalSteps || 1, p_assignee: p.assignee || null,
});
export const procAdvance = (id, step) =>
  call("proc_run_advance", { p_id: id, p_step: step ?? null });
export const procComplete = (id, result) =>
  call("proc_run_complete", { p_id: id, p_result: result || "Completed" });
export const procAbandon = (id, reason) =>
  call("proc_run_abandon", { p_id: id, p_reason: reason });

export const canWrite = () => isConfigured;

// ── Journal control ─────────────────────────────────────────────────────────
// Journals post immediately, which is Affinity's policy, and an approval
// mechanism exists that can be switched on per entity with a threshold. None
// of it was reachable: bk_journal_approve, bk_journal_reject and
// bk_journal_reverse were built and had no button.
//
// So if a threshold were ever set, journals above it would queue for an
// approval nobody could give — the control would stop work rather than
// govern it.

export const journalApprove = (journalId) =>
  call("bk_journal_approve", { p_journal: journalId });

export const journalReject = (journalId, reason) =>
  call("bk_journal_reject", { p_journal: journalId, p_reason: reason });

// REVERSING IS NOT DELETING. A posted journal is never removed — a reversing
// entry is posted against it, dated, with a reason. Deleting one would
// unbalance the ledger and leave nothing to explain why the figures changed.
export const journalReverse = (journalId, reason, date) =>
  call("bk_journal_reverse", { p_journal: journalId, p_reason: reason, p_date: date });

// ── Billing and timesheet corrections ───────────────────────────────────────
// run_billing turns approved, unbilled WIP into invoices up to a date. WIP was
// visible, invoices could be raised by hand, and the thing that connects them
// had no button — so the bridge between the time recorded and the fee charged
// was missing.
export const runBilling = (entityId, upTo) =>
  call("run_billing", { p_entity_id: entityId, p_up_to: upTo, p_created_by: null });

// Correcting a time entry before it is submitted. Time could be recorded and
// submitted and never corrected, so a mistyped hour or a thin narrative stayed
// as it was — and the narrative is what appears on the client's invoice.
export const tsEntryUpdate = (id, hours, matter, narrative, billable) =>
  call("ts_entry_update", { p_id: id, p_hours: hours, p_matter: matter,
                            p_narrative: narrative, p_billable: billable });

// ── Crediting an invoice, and chasing ───────────────────────────────────────
// A credit note is its own document raised AGAINST an invoice. Editing an
// invoice to reverse it destroys the audit trail, and the client's account
// then shows a figure that never existed.
export const invCreditNote = (invoiceId, reason) =>
  call("inv_credit_note", { p_invoice: invoiceId, p_reason: reason });

// Recording that a debt was chased. The count is the useful part: three
// chases with no response is a different conversation from one, and without a
// record every chase starts from nothing.
export const collectionActionLog = (c) => call("log_collection_action", {
  p_customer: c.customer, p_invoice: c.invoiceId ?? null, p_date: c.date,
  p_level: c.level, p_note: c.note || null, p_user: null,
});

// proceduresList and procedureRuns already exist in affinity_ops_api.js. I
// nearly added a second copy here before checking — the same duplicate-wrapper
// mistake this audit has found three times in other people's code.
